/// <amd-module name="PipelinesUI/Components/LogViewer/LogViewer" />
import * as Resources from "PipelinesUI/Resources";
import { LogParser } from "PipelinesUI/Utilities/LogParser";
import {
    IFindResult,
    IFindSectionMark,
    ILine,
    IParsedFindResult,
    IParser,
    ISectionFind,
    SectionMarkerType
} from "PipelinesUI/Utilities/LogParser.Types";
import { getText, Parser, TimestampRegex } from "PipelinesUI/Utilities/Parser";
import * as React from "react";
import { IObservableArrayEventArgs, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { Icon, IIconProps } from "VSSUI/Icon";
import { Intersection } from "VSSUI/Intersection";
import { Measure } from "VSSUI/Measure";
import { IObservableExpression, Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { FixedHeightTree, ITreeRowDetails } from "VSSUI/TreeEx";
import { css } from "VSSUI/Util";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { LineTransform } from "./LineTransform";
import { ILogViewerProps, ISection, ISectionLine, ISelection } from "./LogViewer.Props";

// stop complaining tslint! our rules don't let to do the things we do here..
// we follow this ReleaseManagement/bld/CDPScripts/tslint.json apparently, so with out this, it will fail RM.Build.UX-TsLint...
// Also see this - https://dev.azure.com/mseng/AzureDevOps/_build/results?buildId=9080216&_a=summary,
//  says - ERROR: ../../../../../DistributedTask/Web/extensions/vss-distributedtask-web/pipelines-ui/run-logs/Reader.tsx:363:13 - the "this" keyword is disallowed outside of a class body
//          NOT TRUE!
/* tslint:disable*/

interface ISectionData {
    content: string;
    lines: ILine[];
    findResult: IFindResult | undefined;
    treeItems: ITreeItem<ILine>[];
    treeProvider: TreeItemProvider<ILine>;
    treeRef?: React.RefObject<FixedHeightTree<ILine>>;
    largestRowWidth: number;
    showTimestamps: boolean;
}

const dataLine = "data-line";
const dataSection = "data-sec";
const dataLineSection = "data-lsec";

const mainContentClass = "reader-main-content";

interface ILineProps {
    index: number;
    sectionKey: string;
    className: string;
    line: number;
    content: JSX.Element | null;
    onClick?: (event: React.MouseEvent<HTMLSpanElement>) => void;
    onCopyLink?: (event: React.MouseEvent<HTMLSpanElement>) => void;
    isSnap?: boolean;
    copy?: ICopy;
}

interface ICopy {
    key: string;
    line: number;
}

const Line = (props: ILineProps) => {
    // note: we have a test case in AnsiParser.test.ts that tests for XSS
    const copied = props.copy && props.copy.key === props.sectionKey && props.copy.line === props.line;
    const Element = props.onClick ? Button : "span";
    return (
        <Element
            key={props.index}
            className={css("line-area flex-center flex-row flex-grow justify-start", props.className, props.onClick && "cursor-pointer")}
            role="group"
            {...(props.onClick
                ? {
                      onClick: props.onClick
                  }
                : {})}
        >
            <span
                className="flex-noshrink flex-self-start line"
                data-lsec={props.sectionKey}
                data-line={props.line}
                role="treeitem"
                {...(props.isSnap
                    ? {
                          "data-snap": "1"
                      }
                    : {})}
                {...(props.onCopyLink
                    ? {
                          onClick: props.onCopyLink
                      }
                    : {})}
            ></span>
            {props.content && <span className="content">{props.content}</span>}
            {props.onCopyLink && (
                <Tooltip text={copied ? Resources.Copied : Resources.CopyLink}>
                    <span className="link" data-lsec={props.sectionKey} data-line={props.line} onClick={props.onCopyLink}>
                        <Icon iconName="Link" className="fontSizeM" />
                    </span>
                </Tooltip>
            )}
        </Element>
    );
};

/**
 * Hosts log component, note that this is made to work inside a panel, some calculations might have to check to work else where
 * This virtualizes lines using the List component.
 */
export class LogViewer extends React.Component<ILogViewerProps> {
    private _parser: IParser;
    private _subParser = new Parser();
    private _container = React.createRef<HTMLDivElement>();

    // temporary toline to scroll to a line once
    private _toLine: ISectionLine | undefined;
    // temporary toline to scroll to line end
    private _autoScrollLine: ISectionLine | undefined;

    // Needed to support sticky horizontal section header.
    private _viewerWidth = new ObservableValue<number>(0);
    // The largest row may be virtualized, so we need to keep track of the largest row width to set the horizontal scrollbar.
    private _largestRowWidth = new ObservableValue<number>(0);
    // line to scroll controlled by find
    private _findLine = new ObservableValue<ISectionLine | undefined>(undefined);

    private _find: Find = new Find();
    private _findText: string = "";
    private _scrollPending: boolean = false;

    private _selection: ISelection | undefined;
    private _copy: ICopy | undefined;
    private _autoScroll: boolean = true;

    // The maximum height of the table when virtualized. Browsers have issues
    // rendering elements that are too large and when the LogViewer contains thousands
    // of elements, the list renders very large spacer elements to correctly
    // position the scroll bar. The large spacer elements cause rendering issues
    // across browsers. To bypass this, we need to limit how large the list can
    // grow to. By default this size is 8,500,000px based of Mozilla Firefox restrictions.
    // Other browsers based on Chromium like Chrome, Opera, Edge etc can
    // render up to 30,000,000px
    private _maxHeight: number = 8500000;

    // observables for sections
    private _showLog: { [sectionKey: string]: ObservableValue<boolean | undefined> } = {};
    private _data: { [sectionKey: string]: ObservableValue<ISectionData> } = {};
    private _sections: { [sectionKey: string]: ObservableValue<ISection> } = {};
    private _selectionChanged: { [sectionKey: string]: ObservableValue<boolean> } = {};
    private _copyChanged: { [sectionKey: string]: ObservableValue<boolean> } = {};

    // content pending tracking for sections
    private _pending: { [sectionKey: string]: boolean } = {};

    private _sectionTreeRef: { [sectionKey: string]: React.RefObject<FixedHeightTree<ILine>> } = {};
    private _scrollable: HTMLElement | undefined;

    private _toSelectAll: string | undefined;

    constructor(props: ILogViewerProps) {
        super(props);
        this._parser = new LogParser();
        this.state = {
            findActive: false
        };
        this._initializeSections(this.props.sections.value);
    }

    public render() {
        return (
            <Observer
                sections={{ observableValue: this.props.sections, filter: this._onSectionsChanged } as IObservableExpression<ISection[]>}
                find={this._find.result}
            >
                {(props: { sections: ISection[] }) => {
                    const sections = props.sections;
                    return (
                        <Intersection>
                            <div
                                aria-label={Resources.LogSection}
                                className={`pl-reader fontSize ${this.props.className}`}
                                onScroll={this._onScroll}
                                ref={this._container}
                                onKeyDown={this._containerKeyDown}
                            >
                                <Measure onMeasure={this._onMeasureContainer}>
                                    <div className={`${mainContentClass} flex-grow`}>
                                        <ol>
                                            {sections.map((sectionFromObservable) => {
                                                const sectionKey = sectionFromObservable.key;
                                                return (
                                                    <li key={sectionKey} data-sec={sectionKey}>
                                                        <Observer
                                                            findLine={this._find.result}
                                                            showLog={this._showLog[sectionKey]}
                                                            data={this._data[sectionKey]}
                                                            section={this._sections[sectionKey]}
                                                            largestRowWidth={this._largestRowWidth}
                                                            viewerWidth={this._viewerWidth}
                                                        >
                                                            {(sectionProps: {
                                                                findLine: IFindResult | undefined;
                                                                showLog: boolean | undefined;
                                                                data: ISectionData;
                                                                section: ISection;
                                                                largestRowWidth: number;
                                                                viewerWidth: number;
                                                            }) => {
                                                                // constructing data that's needed to render lines

                                                                const s = sectionProps.section;
                                                                const lines = sectionProps.data.lines || [];
                                                                const linesToRender = sectionProps.data.lines || [];
                                                                const showLog = sectionProps.showLog && linesToRender.length > 0;
                                                                const showSpinner = !!(
                                                                    this._showLog[sectionKey] &&
                                                                    this._showLog[sectionKey].value &&
                                                                    linesToRender.length == 0
                                                                );
                                                                const hasContent = s.hasData || lines.length > 0;
                                                                const noStatusFaint = s.statusProps && s.statusProps === Statuses.Failed;

                                                                // constructing data that's needed to parse find results
                                                                let parsedFindResult: IParsedFindResult | undefined = undefined;
                                                                const dataFindResult = sectionProps.findLine;
                                                                if (dataFindResult) {
                                                                    const marker = dataFindResult.markers[sectionKey];
                                                                    if (marker) {
                                                                        parsedFindResult = { ...dataFindResult } as IParsedFindResult;
                                                                        // send slice of lines that belong to this section
                                                                        parsedFindResult.lines = dataFindResult.lines;
                                                                        // send selected line
                                                                        const selectedLine = dataFindResult.lines[dataFindResult.toScrollIndex];
                                                                        if (selectedLine && selectedLine.key === sectionKey) {
                                                                            parsedFindResult.selectedLine = selectedLine.line;
                                                                        }
                                                                    }
                                                                }

                                                                const controlId = sectionKey + "-log";

                                                                const rowWidth =
                                                                    sectionProps.largestRowWidth <= sectionProps.viewerWidth
                                                                        ? "100%"
                                                                        : `${sectionProps.largestRowWidth}px`;
                                                                // we can't use flex here since we need position:sticky to work, so we calculate the width of the section header.
                                                                let buttonWidth =
                                                                    sectionProps.viewerWidth -
                                                                    (24 + 1) * 2; /* 24 padding - 1px border on each side */

                                                                return (
                                                                    <div
                                                                        className={css(
                                                                            "flex-column content-area",
                                                                            s.getLineLink && "linkable",
                                                                            s.className
                                                                        )}
                                                                        style={{ width: rowWidth }}
                                                                    >
                                                                        <div className="flex-row sec-sticky">
                                                                            <Button
                                                                                className={css(
                                                                                    "flex flex-row sec-area",
                                                                                    hasContent && "cursor-pointer",
                                                                                    showLog && "expand",
                                                                                    s.hidden && "hide"
                                                                                )}
                                                                                ariaControls={controlId}
                                                                                disabled={!hasContent}
                                                                                ariaExpanded={showLog}
                                                                                {...(hasContent
                                                                                    ? { onClick: (e) => this._onToggleLog(sectionKey) }
                                                                                    : {})}
                                                                            >
                                                                                <div
                                                                                    className="status-area flex flex-grow"
                                                                                    style={{ width: buttonWidth + "px" }}
                                                                                >
                                                                                    <div className="flex-grow flex-self-center flex flex-row sec-content">
                                                                                        <SecStatus
                                                                                            iconProps={s.iconProps}
                                                                                            statusProps={s.statusProps}
                                                                                            spinner={showSpinner}
                                                                                            noStatusFaint={noStatusFaint}
                                                                                        />
                                                                                        <span className="title text-ellipsis body-m">{s.title}</span>
                                                                                    </div>
                                                                                    {s.getFarRight && (
                                                                                        <div className="far-right body-s flex-self-center">
                                                                                            {s.getFarRight(s)}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </Button>
                                                                            <div className="flex-row flex-grow" />
                                                                        </div>
                                                                        {showSpinner ? (
                                                                            <Spinner size={SpinnerSize.large} label={Resources.SpinnerLabel}/>
                                                                        ) : (
                                                                            <FixedHeightTree<ILine>
                                                                                id={controlId}
                                                                                className={css(!showLog && "hide")}
                                                                                focuszoneProps={{
                                                                                    activateOnEnter: !!s.getLineLink
                                                                                }}
                                                                                itemProvider={
                                                                                    showLog
                                                                                        ? sectionProps.data.treeProvider
                                                                                        : new TreeItemProvider([])
                                                                                }
                                                                                maxHeight={this._maxHeight}
                                                                                onToggle={(
                                                                                    event: React.SyntheticEvent<HTMLElement>,
                                                                                    treeItem: ITreeItemEx<ILine>
                                                                                ) => {
                                                                                    if (
                                                                                        treeItem.underlyingItem.childItems &&
                                                                                        sectionProps.data.treeProvider &&
                                                                                        !event.defaultPrevented
                                                                                    ) {
                                                                                        sectionProps.data.treeProvider.toggle(
                                                                                            treeItem.underlyingItem
                                                                                        );
                                                                                        event.preventDefault();
                                                                                    }
                                                                                }}
                                                                                pageSize={60}
                                                                                renderLoadingRow={(
                                                                                    rowIndex: number,
                                                                                    rowDetails: ITreeRowDetails<ILine>
                                                                                ) => {
                                                                                    const asyncInfo = sectionProps.section.asyncInfo;
                                                                                    if (asyncInfo) {
                                                                                        asyncInfo.getMore(asyncInfo.payload);
                                                                                        return (
                                                                                            <div className="flex-row flex-grow">
                                                                                                <div className="shimmer shimmer-line log-async-loading" />
                                                                                            </div>
                                                                                        );
                                                                                    }
                                                                                    return <div />;
                                                                                }}
                                                                                renderRow={(
                                                                                    index: number,
                                                                                    item: ITreeItemEx<ILine>,
                                                                                    details: ITreeRowDetails<ILine>
                                                                                ) => {
                                                                                    return (
                                                                                        <Observer
                                                                                            findResult={this._find.result}
                                                                                            x={this._copyChanged[sectionKey]}
                                                                                        >
                                                                                            {(rowProps: { findResult: IFindResult | undefined }) => {
                                                                                                const lineNumber = item.underlyingItem.data.lineNumber;
                                                                                                const isSelected =
                                                                                                    this._selection &&
                                                                                                    this._selection.key === sectionKey;
                                                                                                const isLineSelected =
                                                                                                    isSelected &&
                                                                                                    (this._selection as ISelection).lineToScroll ===
                                                                                                    lineNumber;
                                                                                                const isCopiedLine =
                                                                                                    this._copy && this._copy.line === lineNumber;
                                                                                                const extraClassName =
                                                                                                    isLineSelected || isCopiedLine ? "highlight" : "";

                                                                                                const dataFindResult = rowProps.findResult;
                                                                                                let lineParsedResult = undefined;
                                                                                                if (dataFindResult) {
                                                                                                    const marker = dataFindResult.markers[sectionKey];
                                                                                                    if (marker) {
                                                                                                        lineParsedResult = {
                                                                                                            ...dataFindResult
                                                                                                        } as IParsedFindResult;
                                                                                                        // send slice of lines that belong to this section
                                                                                                        lineParsedResult.lines = dataFindResult.lines;
                                                                                                        // send selected line
                                                                                                        const selectedLine =
                                                                                                            dataFindResult.lines[
                                                                                                                dataFindResult.toScrollIndex
                                                                                                            ];
                                                                                                        if (
                                                                                                            selectedLine &&
                                                                                                            selectedLine.key === sectionKey
                                                                                                        ) {
                                                                                                            lineParsedResult.selectedLine =
                                                                                                                selectedLine.line;
                                                                                                        }
                                                                                                    }
                                                                                                }

                                                                                                const transformedLine = (
                                                                                                    <LineTransform
                                                                                                        expanded={item.underlyingItem.expanded}
                                                                                                        section={sectionKey}
                                                                                                        line={item.underlyingItem.data}
                                                                                                        pureHTML={s.showHtml}
                                                                                                        subParser={this._subParser}
                                                                                                        raw={sectionProps.data.content}
                                                                                                        lineNumber={lineNumber}
                                                                                                        findResult={lineParsedResult}
                                                                                                        contentId={`rowContent-${index}`}
                                                                                                    />
                                                                                                );

                                                                                                return (
                                                                                                    <div
                                                                                                        className={css(
                                                                                                            "line-row flex-row flex-grow",
                                                                                                            item.underlyingItem.childItems &&
                                                                                                                "group-item"
                                                                                                        )}
                                                                                                    >
                                                                                                        <Line
                                                                                                            index={lineNumber}
                                                                                                            sectionKey={sectionKey}
                                                                                                            className={extraClassName}
                                                                                                            line={item.underlyingItem.data.lineNumber}
                                                                                                            content={transformedLine}
                                                                                                            onCopyLink={
                                                                                                                s.getLineLink && this._copyLink
                                                                                                            }
                                                                                                            copy={this._copy}
                                                                                                        />
                                                                                                    </div>
                                                                                                );
                                                                                            }}
                                                                                        </Observer>
                                                                                    );
                                                                                }}
                                                                                rowHeight={20}
                                                                                ref={sectionProps.data.treeRef}
                                                                            />
                                                                        )}
                                                                        {!showLog && s.snaps && s.snaps.length > 0 && (
                                                                            <div className="lines-content">
                                                                                {s.snaps.map((snap, index) => {
                                                                                    const lines = this._parser.parse(snap.content);
                                                                                    // snap content is supposed to have only one line
                                                                                    const lineData = lines[0];
                                                                                    const transformed = (
                                                                                        <LineTransform
                                                                                            section={sectionKey}
                                                                                            line={lineData}
                                                                                            subParser={this._subParser}
                                                                                            raw={snap.content}
                                                                                            lineNumber={snap.line}
                                                                                            findResult={parsedFindResult}
                                                                                        />
                                                                                    );

                                                                                    return (
                                                                                        transformed && (
                                                                                            <Line
                                                                                                index={index}
                                                                                                sectionKey={sectionKey}
                                                                                                key={sectionKey + index}
                                                                                                className={"snap"}
                                                                                                isSnap={true}
                                                                                                line={snap.line}
                                                                                                content={transformed}
                                                                                                onClick={this._onSnapLineClick}
                                                                                                onCopyLink={s.getLineLink && this._copyLink}
                                                                                                copy={this._copy}
                                                                                            />
                                                                                        )
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }}
                                                        </Observer>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    </div>
                                </Measure>
                            </div>
                        </Intersection>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        const sectionToGet = this._selection && this._selection.key;
        if (sectionToGet) {
            this._setContent(sectionToGet);
        }

        if (this._container.current) {
            this._container.current.focus();
        }

        this._find.result.subscribe(this._onFind);
    }

    public componentWillUnmount() {
        this._unregisterScrollable();
        this._find.result.unsubscribe(this._onFind);
    }

    /**
     * This can handle situations that could break user expectations with scrolling
     * Eg: Pressing "End" should go the true end
     * @param element Elements that's scrollable
     */
    public initializeScrollable(element: HTMLElement) {
        this._scrollable = element;
        this._scrollable.addEventListener("keydown", this._bindKeys);
    }

    /**
     * Selects a section/line
     * @param key section to select
     * @param lineNumber linenumber to scroll to in the specified section
     */
    public select(key: string, lineNumber?: number) {
        if (!this._showLog[key]) {
            // bad selection
            return;
        }

        if (lineNumber) {
            this._selection = {
                key: key,
                lineToScroll: lineNumber
            };
            this._toLine = {
                key: key,
                line: lineNumber
            };
            this._scrollPending = true;
        } else {
            // if linenumber is not given, "unselect" the existing line selection if it exists
            this._selection = undefined;
        }

        // After we initialize lines to scroll as needed, make sure that we show this section, note that we don't collapse once we expanded, so there's no way to "unselect" an expanded section/task
        this._setContent(key);

        // notify observer to re-render that section
        this._selectionChanged[key].value = !this._selectionChanged[key].value;

        this._toSelectAll = key;
    }

    /**
     * Updates the content in the section by force calling getData() to get new content and auto expanding the section if the user didn't explictly interacted with the section
     * If this section already sent hasContent as well, there's no need for the section to update that, we handle the case where we have data even if hasContent is false
     * @param key section to update content for
     */
    public updateContent(key: string) {
        // updates the content for section at key only if there's no existing state/ user didn't interact with it yet
        const existingValue = this._showLog[key] && this._showLog[key].value;
        if (existingValue === undefined || existingValue === true) {
            this._setContent(key, true);
        }
    }

    /**
     * Brings focus to the reader
     */
    public focus() {
        this._container.current && this._container.current.focus();
    }

    /**
     * Scrolls to the very top of the view, handles virtualizations so that we are at the true top
     */
    public toHome = () => {
        this._noScroll();
        this._jump(true);
    };

    /**
     * Scrolls to the very end of the view, handles virtualizations so that we are at the true end
     */
    public toEnd = () => {
        this._jump();
    };

    /**
     * Finds the supplied text with in expanded content
     */
    public find(text: string) {
        this._noScroll();
        this._findText = text;
        this._find.find(this._findText, this._getFindProps());
    }

    /**
     * When find is active, moves to the next match
     */
    public findNext() {
        if (this._findText) {
            this._find.down();
        }
    }

    /**
     * Clears find
     */
    public clearFind() {
        if (this._find.result) {
            this._find.result.value = undefined;
        }
    }

    /**
     * When find is active, moves to the previous match
     */
    public findPrev() {
        if (this._findText) {
            this._find.up();
        }
    }

    /**
     * Gets find result observable, consumers can subscribe to this to listen to result changes
     */
    public getFindResult() {
        return this._find.result;
    }

    private _noScroll() {
        this._autoScroll = false;
        delete this._autoScrollLine;
    }

    private _containerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // override select all to only select the text from the current focused section if exists
        if (this._toSelectAll && (event.ctrlKey || event.metaKey) && event.key === "a") {
            const currentSection = document.querySelector(`[${dataSection}="${this._toSelectAll}"]`) as HTMLElement;
            if (currentSection) {
                const selection = window.getSelection() as Selection;
                const range = document.createRange();
                range.selectNodeContents(currentSection);
                selection.removeAllRanges();
                selection.addRange(range);
                event.preventDefault();
            }
        }
    };

    private _getTreeItemsFromLines(lines: ILine[]): ITreeItem<ILine>[] {
        let group: { [groupNo: number]: ITreeItem<ILine> } = {};
        let groupIndex = -1;
        let treeItems: ITreeItem<ILine>[] = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const node = line.nodes[0];
            if (line.nodes.some((n) => !!n.isGroup)) {
                if (groupIndex != -1) {
                    // add previous group
                    treeItems.push(group[groupIndex]);
                    groupIndex = -1;
                }
                groupIndex = line.nodes[0].lineIndex;
                group[groupIndex] = { data: line };
            } else if (node.group && group[node.group.lineIndex]) {
                const groupTreeItem = group[node.group.lineIndex];
                if (groupTreeItem.childItems) {
                    groupTreeItem.childItems.push({ data: line });
                } else {
                    groupTreeItem.childItems = [{ data: line }];
                }
            } else {
                if (groupIndex != -1) {
                    // add previous group
                    treeItems.push(group[groupIndex]);
                    groupIndex = -1;
                }

                treeItems.push({ data: line });
            }
        }
        if (groupIndex != -1) {
            treeItems.push(group[groupIndex]);
        }

        return treeItems;
    }

    private _jump(top: boolean = false) {
        const allSections = this.props.sections.value || [];
        const section = top ? allSections[0] : allSections[allSections.length - 1];
        this._goTo(section, top);
    }

    private _getTreeRef = (sectionKey: string) => {
        if (!this._sectionTreeRef[sectionKey]) {
            this._sectionTreeRef[sectionKey] = React.createRef<FixedHeightTree<ILine>>();
        }

        return this._sectionTreeRef[sectionKey];
    };

    /**
     * Scrolls to the section specified, handles virtualization
     * @param section section to focus on
     * @param top whether to focus on the top or bottom of the section if there's only one
     */
    private _goTo(section: ISection, top: boolean) {
        if (this._container.current && section) {
            // ignore any intersections
            let element = this._container.current.querySelector(`[${dataSection}="${section.key}"]`) as HTMLElement;
            const allSections = this.props.sections.value || [];
            if (element) {
                // scroll to section if there are more than 1
                allSections.length > 1 &&
                    element.scrollIntoView({
                        behavior: "auto",
                        block: "center"
                    });

                allSections.length == 1 && element.scrollIntoView(top);
            }
        }
    }

    private _bindKeys = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        // eg: pressing Home inside search box
        if (target && target.tagName === "INPUT") {
            return;
        }

        if (event.key === "Home") {
            event.preventDefault();
            this.toHome();
        } else if (event.key === "End") {
            event.preventDefault();
            this.toEnd();
        }
    };

    private _unregisterScrollable() {
        if (this._scrollable) {
            this._scrollable.removeEventListener("keydown", this._bindKeys);
        }
    }

    private _initializeSections(sections: ISection[], isUpdate: boolean = false) {
        const selectedSection = this._selection ? this._selection.key : null;
        sections.forEach((s) => {
            if (this._sections[s.key]) {
                // propogate updates for existing item
                this._sections[s.key].value = s;

                if (isUpdate) {
                    // force update content if this is already visible
                    if (this._showLog[s.key].value) {
                        this._setContent(s.key, true);
                    }

                    return;
                }
            } else {
                this._sections[s.key] = new ObservableValue<ISection>(s);
            }

            const value = selectedSection === s.key || s.isExpanded ? true : undefined;
            if (this._showLog[s.key]) {
                this._showLog[s.key].value = value;
            } else {
                this._showLog[s.key] = new ObservableValue<boolean | undefined>(value);
            }

            if (this._data[s.key]) {
                this._data[s.key].value = {} as ISectionData;
            } else {
                this._data[s.key] = new ObservableValue<ISectionData>({} as ISectionData);
            }

            if (this._selectionChanged[s.key]) {
                this._selectionChanged[s.key].value = false;
            } else {
                this._selectionChanged[s.key] = new ObservableValue<boolean>(false);
            }

            if (this._copyChanged[s.key]) {
                this._copyChanged[s.key].value = false;
            } else {
                this._copyChanged[s.key] = new ObservableValue<boolean>(false);
            }

            if (s.isExpanded) {
                this._setContent(s.key);
            }
        });
    }

    private _onMeasureContainer = (newWidth: number, newHeight: number) => {
        if (this._viewerWidth.value !== newWidth) {
            this._viewerWidth.value = newWidth;
            if (this._viewerWidth.value > this._largestRowWidth.value) {
                this._largestRowWidth.value = this._viewerWidth.value;
            }
        }
    };

    private _onSectionsChanged = (newData: IObservableArrayEventArgs<ISection>) => {
        (newData.removedItems || []).forEach((x) => {
            delete this._showLog[x.key];
            delete this._data[x.key];
            delete this._selectionChanged[x.key];
            delete this._copyChanged[x.key];
            delete this._sections[x.key];
        });

        const newItems = newData.addedItems || [];
        const updatedItems = newData.changedItems || [];

        // initialize new items and propogate updates to existing items
        this._initializeSections(newItems.concat(updatedItems), true);

        // update observable state if there are any new items, else update to existing items will be taken care by _initializeSections
        return newItems.length > 0;
    };

    private _scroll(section: ISectionData, lineNumber: number, placement: ScrollLogicalPosition = "center", done?: () => void) {
        const index = lineNumber - 1;
        if (section && section.treeItems && section.treeRef && section.treeRef.current) {
            let treeItem = this._findFromIndex(index, section.treeItems);
            treeItem && section.treeProvider && section.treeProvider.expand(treeItem, true);
            section.treeRef.current.scrollIntoView(index, { block: placement });
        }
        // Run done() after the next animation frame so onScroll runs before we clean up.
        requestAnimationFrame(() => {
            done && done();
        });
    }

    private _recountLineIndexIfHasGroups(index: number, content: string) {
        var arrayOfStrings = content.split("\r\n");
        for (let i = 0; i < arrayOfStrings.length && index > i; i++) {
            if (arrayOfStrings[i] == "##[endgroup]") {
                index--;
            }
        }
        return index;
    }

    private _findFromIndex = (index: number, lines?: ITreeItem<ILine>[]): ITreeItem<ILine> | undefined => {
        if (lines && lines.length > 0) {
            for (let i = 0; i < lines.length; i++) {
                let lineIndex = lines[i].data.nodes[0].lineIndex;
                if (lineIndex === index) {
                    return lines[i];
                }
                // the index is in the children if it is the last item or the next node is too big.
                else if (!lines[i + 1] || lines[i + 1].data.nodes[0].lineIndex > index) {
                    return this._findFromIndex(index, lines[i].childItems);
                }
            }
        }
        return undefined;
    };

    private _reFind = () => {
        if (this._findText) {
            requestAnimationFrame(() => {
                this._find.find(this._findText, this._getFindProps());
            });
        }
    };

    private _getFindProps(): IFindProps {
        let lines: ILine[] = [];
        let sectionMarkers: IFindSectionMark[] = [];
        let sectionLimits: IFindSectionMark[] = [];
        let sectionKeys: string[] = [];
        Object.keys(this._sections).forEach((sKey) => {
            // get only expanded sections information
            const sectionData = this._showLog[sKey] && this._showLog[sKey].value && this._data[sKey] && this._data[sKey].value;
            if (sectionData) {
                sectionKeys.push(sKey);
                const listStats = sectionData.treeRef && sectionData.treeRef.current && sectionData.treeRef.current.getStats();
                listStats &&
                    sectionLimits.push({
                        startIndex: listStats.firstMaterialized,
                        endIndex: listStats.lastMaterialized
                    });
                const markerStart = Math.max(lines.length - 1, 0);
                lines = lines.concat(sectionData.lines);
                const markerEnd = Math.max(lines.length - 1, 0);
                sectionMarkers.push({
                    startIndex: markerStart,
                    endIndex: markerEnd
                });
            }
        });
        return {
            getContent: this._getFindContent,
            lines: lines,
            sectionKeys: sectionKeys,
            sectionMarkers: sectionMarkers,
            sectionLimits: sectionLimits
        };
    }

    private _getFindContent = (key: string) => {
        if (this._data[key]) {
            return this._data[key].value.content;
        }
        return "";
    };

    private _onFind = (result?: IFindResult) => {
        if (result && result.lines.length > 0) {
            const resultLine = result.lines[result.toScrollIndex];
            const sectionData = this._data[resultLine.key] && this._data[resultLine.key].value;
            if (sectionData) {
                this._findLine.value = {
                    key: resultLine.key,
                    line: resultLine.line
                };
                this._scrollPending = true;
                sectionData.findResult = result;
                this._scrollToLine();
            }
        } else {
            this._findLine.value = undefined;
            // get all expanded sections and update data
            Object.keys(this._showLog).forEach((x) => {
                let newData = { ...this._data[x].value };
                newData.findResult = undefined;
                this._data[x].value = newData;
            });
        }
    };

    private _onToggleLog(key: string) {
        this._showLog[key].value = !this._showLog[key].value;

        if (this._showLog[key].value) {
            this._setContent(key);

            requestAnimationFrame(() => {
                this._scroll(this._data[key].value, 0, "nearest");
            });
        }

        let largestWidth = this._viewerWidth.value - 20;

        this.props.sections.value.forEach((section) => {
            let sectionData: ISectionData = this._data[section.key].value;
            if (this._showLog[section.key].value && !!sectionData.largestRowWidth) largestWidth = Math.max(largestWidth, sectionData.largestRowWidth);
        });

        this._largestRowWidth.value = largestWidth;

        this._reFind();
    }

    private _onScroll = () => {
        const scrollElement = this._container.current;
        if (this._scrollPending) {
            return;
        }

        // If the user has scrolled to the current section that is auto-scrolling, enable autoscroll.
        let lastLine = false;
        if (this._autoScrollLine) {
            const tree = this._getTreeRef(this._autoScrollLine.key).current;
            const stats = tree && tree.getStats();
            lastLine = this._autoScrollLine.line === (stats && stats.lastMaterialized + 1);
        }

        // If the user has scrolled within 25px of the end of the scroll element, trigger autoscroll. We may want to fine tune this in the future.
        this._autoScroll =
            lastLine ||
            (!!this._sections && !!scrollElement && scrollElement.scrollHeight - scrollElement.offsetHeight - scrollElement.scrollTop < 25);
    };

    private _onSnapLineClick = (event: React.MouseEvent<HTMLSpanElement>) => {
        if (event) {
            const liElement = event.currentTarget as HTMLLIElement;
            if (liElement) {
                event.stopPropagation();
                const element = liElement.querySelector(".link");
                if (element) {
                    const sectionKey = element.getAttribute(dataLineSection) || "";
                    this._sections[sectionKey].value.isExpanded = false;
                    const number = parseInt(element.getAttribute(dataLine) || "");
                    this.select(sectionKey, number);
                }
            }
        }
    };

    private _copyLink = (event: React.MouseEvent<HTMLSpanElement>) => {
        if (event) {
            const linkElement = (event.target as HTMLSpanElement)?.closest('.link');
            if (linkElement) {
                event.stopPropagation();
                const sectionKey = linkElement.getAttribute(dataLineSection) || "";
                const number = parseInt(linkElement.getAttribute(dataLine) || "");
                const section = this.props.sections.value.find((x) => x.key === sectionKey);
                if (section && section.getLineLink) {
                    const link = section.getLineLink(section, number);
                    // copy link
                    // IE11 Compatible
                    if ((window as any).clipboardData) {
                        (window as any).clipboardData.setData("text", link);
                    }

                    const listener = (e: ClipboardEvent) => {
                        if (e.clipboardData) {
                            e.clipboardData.setData("text", link);
                        }

                        e.preventDefault();
                    };
                    document.addEventListener("copy", listener);
                    const result = document.execCommand("copy");
                    document.removeEventListener("copy", listener);
                    if (result) {
                        this._copy = {
                            key: sectionKey,
                            line: number
                        };
                        this._copyChanged[sectionKey].value = !this._copyChanged[sectionKey].value;
                        window.history.replaceState(window.history.state, document.title, link);
                    }
                }
            }
        }
    };

    /**
     * Makes sure that section is expanded and fetches the content based on props, once fetched, this makes no attempt to get new content again unless 'force' is sent
     * @param key section to expand/show content
     * @param force If true, fetches content from props everytime, defaults to false
     */
    private _setContent(key: string, force: boolean = false) {
        const section = this.props.sections.value.find((x) => x.key === key);
        if (section) {
            if (!this._showLog[key].value) {
                this._showLog[key].value = true;
            }

            const sectionData = this._data[key] && this._data[key].value;
            if (force || !sectionData.content) {
                this._pending[section.key] = true;

                const data = section.getData();
                if (data) {
                    if ((data as Partial<Promise<string>>).then) {
                        (data as Promise<string>).then((data) => {
                            this._onData(section, key, data, force);
                        });
                    } else {
                        this._onData(section, key, data as string, force);
                    }
                }
            } else {
                this._onData(section, key, sectionData.content);
            }
        }
    }

    private _onData(section: ISection, key: string, data: string, force?: boolean) {
        if (!this._data[key]) {
            console.error("section data can't be null", key);
            return;
        }

        const oldData: ISectionData = this._data[key].value;
        let oldLinesLength = 0;
        let content: string = data || "";
        const parser: IParser = section.parser || this._parser;

        let parseAllLines: boolean = oldData.showTimestamps != this.props.showTimestamps;
        if (!parseAllLines && content.indexOf(oldData.content) > 0) {
            parseAllLines = true;
        }

        // replace any timestamps in the beginning of the logs
        content = this.props.showTimestamps ? content : content.replace(TimestampRegex, "");

        // curled double quotes are left after echo command, so it lead to errors of parsing
        content = content.replace(/[“”]/g, '');

        let lines: ILine[];
        if (!parseAllLines) {
            const startParseIndex = oldData.content ? oldData.content.length : 0;
            oldLinesLength = oldData.lines ? oldData.lines.length : 0;
            lines = parser.parse(content, startParseIndex, oldLinesLength);
        } else {
            lines = parser.parse(content);
        }

        let largestRowWidth: number = 0;
        lines.forEach((line) => {
            const nodesCount = line.nodes.length;
            if (nodesCount > 0) {
                const start = line.nodes[0].start;
                const end = line.nodes[nodesCount - 1].end;
                largestRowWidth = Math.max(largestRowWidth, (end - start) * 8 /* 8 is an estimate px width per character */);
            }
        });
        const newTreeItems = this._getTreeItemsFromLines(lines);
        const treeItems = oldData.treeItems && !parseAllLines ? oldData.treeItems.concat(newTreeItems) : newTreeItems;
        const treeItemProvider = new TreeItemProvider(treeItems);

        const asyncInfo = section.asyncInfo;
        if (asyncInfo) {
            if (asyncInfo.hasMore) {
                // if we have more data, inject the shimmer(by making an item as undefined) to the top of the log
                treeItemProvider.value.unshift({
                    underlyingItem: {
                        data: undefined
                    }
                } as any);
            }
        }

        // adjust lize size to fill the view
        const treeRef = this._getTreeRef(key);

        if (this._largestRowWidth.value < largestRowWidth) {
            this._largestRowWidth.value = largestRowWidth;
        }

        let newData: ISectionData = {
            lines: oldLinesLength ? oldData.lines.concat(lines) : lines,
            content: content,
            findResult: oldData.findResult,
            treeProvider: treeItemProvider,
            treeItems: treeItems,
            treeRef: treeRef,
            largestRowWidth: largestRowWidth,
            showTimestamps: !!this.props.showTimestamps
        };

        // update observable
        this._data[key].value = newData;
        !force && this._reFind();

        delete this._pending[section.key];
        if (force && !section.noAutoScroll && this._autoScroll && oldData && oldData.lines && oldData.lines.length < newData.lines.length) {
            this._autoScrollLine = {
                key: key,
                line: newData.lines.length
            };
            this._scrollPending = true;
        } else {
            delete this._autoScrollLine;
        }

        this._scrollToLine();
    }

    private _deferScroll() {
        // This is not a hack, by pushing the scroll to end, we are making sure that the render loop from observable completes and the element is available for us to scroll happily
        requestAnimationFrame(() => {
            this._scrollToLine();
        });
    }

    private _scrollToLine() {
        let lineToScroll = this._toLine || this._findLine.value || this._autoScrollLine;
        if (!lineToScroll || this._pending[lineToScroll.key]) {
            // If the content is still pending no-op
            return;
        }

        const sectionData = lineToScroll && this._data[lineToScroll.key] && this._data[lineToScroll.key].value;
        if (this._scrollPending && lineToScroll && sectionData && sectionData.lines && this._container.current && sectionData.lines.length > 0) {
            // get our section element first
            const section = this._container.current.querySelector(`[${dataSection}="${lineToScroll.key}"]`);
            if (section) {
                const isSnap = section.querySelector('[data-snap="1"]');
                if (isSnap) {
                    // if this is a snap element, defer
                    this._deferScroll();
                    return;
                }

                const cleanUp = () => {
                    this._scrollPending = false;
                    // clean up the temporary lines to scroll
                    if (lineToScroll === this._toLine) {
                        this._toLine = undefined;
                    }
                    if (lineToScroll === this._findLine.value) {
                        this._findLine.value = undefined;
                    }
                };

                let placement: ScrollLogicalPosition = "nearest";
                if (lineToScroll === this._toLine || lineToScroll === this._findLine.value) {
                    // when asked to scroll to a particular line explictly (through URL), place it in center
                    placement = "center";
                }
                if (lineToScroll === this._autoScrollLine) {
                    placement = "end";
                }
                lineToScroll.line = this._recountLineIndexIfHasGroups(lineToScroll.line, sectionData.content);
                sectionData && this._scroll(sectionData, lineToScroll.line, placement, cleanUp);
            }
        }
    }
}

interface ISecStatusProps {
    spinner?: boolean;
    noStatusFaint?: boolean;
    statusProps?: IStatusProps;
    iconProps?: IIconProps;
}

function SecStatus(props: ISecStatusProps) {
    const [spin, setSpin] = React.useState(false);
    React.useLayoutEffect(() => {
        if (props.spinner) {
            const timerId = setTimeout(() => {
                props.spinner && setSpin(true);
            }, 150);
            return () => {
                clearTimeout(timerId);
            };
        } else {
            setSpin(false);
        }
    }, [props.spinner]);

    return (
        <>
            {spin && <Spinner size={SpinnerSize.xSmall} className="status-spinner" />}
            {!spin && (
                <>
                    {props.statusProps && (
                        <Status
                            {...props.statusProps}
                            className={`flex-self-center fontSizeM ${props.noStatusFaint ? "" : "faint"}`}
                            size={StatusSize.m}
                        />
                    )}
                    {props.iconProps && <Icon {...props.iconProps} className="fontSizeML status-icon" />}
                </>
            )}
        </>
    );
}

interface IFindProps {
    getContent(key: string): string;
    lines: ILine[];
    // sections[i] corresponds to sectionKeys[i]
    sectionMarkers: IFindSectionMark[];
    sectionLimits: IFindSectionMark[];
    sectionKeys: string[];
}

class Find {
    private _prevSearchSectionKeys: string[];
    private _prevValue: string | undefined;
    private _toScrollIndex: number = 0;
    private _value: string = "";
    private _result: IObservableValue<IFindResult | undefined> = new ObservableValue<IFindResult | undefined>(undefined);

    public find(text: string, props: IFindProps) {
        this._onChange(null, text, props);
    }

    public get result() {
        return this._result;
    }

    public down() {
        if (this._result.value) {
            const toChange = this._toScrollIndex + 1;
            this._toScrollIndex = toChange > this._result.value.lines.length - 1 ? 0 : toChange;
            var newResult = { ...this._result.value };
            newResult.toScrollIndex = this._toScrollIndex;
            this._result.value = newResult;
        }
    }

    public up() {
        if (this._result.value) {
            const toChange = this._toScrollIndex - 1;
            this._toScrollIndex = toChange < 0 ? this._result.value.lines.length - 1 : toChange;
            var newResult = { ...this._result.value };
            newResult.toScrollIndex = this._toScrollIndex;
            this._result.value = newResult;
        }
    }

    private _onChange = (event: React.ChangeEvent<HTMLElement> | null, value: string, props: IFindProps) => {
        this._value = value;
        // we need to reset the index to scroll if the search text or sections to search in are changed
        if (this._value !== this._prevValue || props.sectionKeys !== this._prevSearchSectionKeys) {
            this._toScrollIndex = 0;
        }

        this._prevValue = value;
        this._prevSearchSectionKeys = props.sectionKeys;

        let result: IFindResult | undefined;
        let findResultLines: ISectionFind[] = [];
        let markers: SectionMarkerType = {};
        if (value) {
            const lines = props.lines;

            // find the actual starting point
            for (let markerIndex = 0; markerIndex < props.sectionMarkers.length; markerIndex++) {
                const markerStart = Math.max(findResultLines.length - 1, -1);
                const sectionKey = props.sectionKeys[markerIndex];
                // lines: ...|sectionStart.....pageStart......sectionEnd|...
                const sectionStart = props.sectionMarkers[markerIndex].startIndex;
                const pageStart = Math.max(props.sectionLimits[markerIndex].startIndex, 0);
                const sectionEnd = props.sectionMarkers[markerIndex].endIndex;

                // we should jump start to the line for this section, to start our search from the line user sees
                // lines: ...|sectionStart.....[pageStart......sectionEnd]|...
                // pageStart to end loop
                for (let index = sectionStart + pageStart; index <= sectionEnd; index++) {
                    const line = lines[index];
                    const lineNumber = markerIndex > 0 ? Math.max(index - sectionStart, 0) : index + 1;
                    this._match(value, sectionKey, line, lineNumber, findResultLines, props);
                }

                // lines: ...|[sectionStart.....]pageStart......sectionEnd|...
                // start to pageStart loop
                if (pageStart !== sectionStart) {
                    for (let index = sectionStart; index <= pageStart; index++) {
                        const line = lines[index];
                        const lineNumber = markerIndex > 0 ? Math.max(index - sectionStart, 0) : index + 1;
                        this._match(value, sectionKey, line, lineNumber, findResultLines, props);
                    }
                }

                const markerEnd = Math.max(findResultLines.length - 1, 0);
                if (findResultLines.length > 0) {
                    markers[sectionKey] = {
                        startIndex: markerStart + 1,
                        endIndex: markerEnd
                    };
                }
            }
        }

        if (findResultLines.length > 0) {
            result = {
                lines: findResultLines,
                text: value,
                toScrollIndex: this._toScrollIndex,
                scrollCount: findResultLines.length,
                markers: markers
            };
        }

        this._result.value = result;
    };

    private _match(value: string, sectionKey: string, line: ILine, lineNumber: number, result: ISectionFind[], props: IFindProps) {
        let foundMatch = false;
        const nodesCount = line.nodes.length;
        if (nodesCount > 0) {
            const start = line.nodes[0].start;
            const end = line.nodes[nodesCount - 1].end;
            const content = props.getContent(sectionKey);
            const data = getText(content.substring(start, end));
            if (data.indexOf(getText(value)) !== -1) {
                foundMatch = true;
            }
        }

        if (foundMatch) {
            result.push({
                key: sectionKey,
                line: lineNumber
            });
        }
    }
}
/* tslint:enable*/
